import { DesignTokenSizeEnum, TokenTextAppearance } from '@volkswagen-onehub/components-core';
import { useTribalContext } from '@/context/tribal';
import FeatureCardsSection from '@/modules/features/compare/FeatureCardsSection/FeatureCardsSection';
import { formatCurrency } from '@/modules/utils/currency';
import { getCarOptionsData } from '@/modules/utils/storeUtils';
import useGlobalStore from '@/stores/globalStore';
import { Box, SectionHeader } from '@/modules/components';
import { StyledKeyFeaturesSections } from './KeyFeatures.styles';
import TyreInformation from '@/modules/features/components/TyreInformation/TyreInformation';
import { FeatureStatus } from '@/hooks/useFeatures';
import { SectionNames } from '@/analytics/sectionNames';

const Features = () => {
  const { contentManager } = useTribalContext();
  const configurations = useGlobalStore((state) => state.app.configurations);

  const getPriceLabel = ({ availability, price }: any) => {
    return availability === FeatureStatus.STANDARD_SPEC
      ? contentManager.get('standard-feature-label')
      : formatCurrency(price);
  };

  const exteriorPaintContent = {
    title: contentManager.get('exterior-option-paint-title'),
    cards: configurations.map((configuration) => {
      if (!configuration.pdbData?.exterior) return {};

      const carOptionsData = getCarOptionsData(
        configuration,
        configuration.pdbData.exterior.code
      );

      return {
        ...carOptionsData,
        body: carOptionsData?.title,
        price: getPriceLabel(carOptionsData),
        decoratorImgContent: 'VW',
      };
    }),
  };

  const exteriorWheelsContent = {
    title: contentManager.get('exterior-option-wheels-title'),
    cards: configurations.map((configuration) => {
      if (!configuration.pdbData?.otherMonoselects) return {};

      const carOptionsData = getCarOptionsData(
        configuration,
        configuration.pdbData.otherMonoselects.wheels
      );

      return {
        ...carOptionsData,
        body: carOptionsData?.title,
        price: getPriceLabel(carOptionsData),
        decoratorImgContent: 'VW',
        secondaryLink: {
          label: contentManager.get('ctas-tyre-information'),
          modalContent: <TyreInformation configuration={configuration} />,
        },
      };
    }),
  };

  const interiorUpholsteryContent = {
    title: contentManager.get('interior-option-upholstery-title'),
    cards: configurations.map((configuration) => {
      if (!configuration.pdbData?.interior) return {};

      const carOptionsData = getCarOptionsData(
        configuration,
        configuration.pdbData.interior.upholstery
      );

      return {
        ...carOptionsData,
        body: carOptionsData?.title,
        price: getPriceLabel(carOptionsData),
        decoratorImgContent: 'VW',
      };
    }),
  };

  const interiorEntertainmentContent = {
    title: contentManager.get('interior-option-in-car-entertainment-title'),
    cards: configurations.map((configuration) => {
      if (!configuration.pdbData?.otherMonoselects) return {};

      const carOptionsData = getCarOptionsData(
        configuration,
        configuration.pdbData.otherMonoselects['in-car-entertainment']
      );

      return {
        ...carOptionsData,
        body: carOptionsData?.title,
        price: getPriceLabel(carOptionsData),
        decoratorImgContent: 'VW',
      };
    }),
  };

  const interiorSteeringWheelContent = {
    title: contentManager.get('interior-option-steering-wheel-title'),
    cards: configurations.map((configuration) => {
      if (!configuration.pdbData?.otherMonoselects) return {};

      const carOptionsData = getCarOptionsData(
        configuration,
        configuration.pdbData.otherMonoselects['steering-wheels']
      );

      return {
        ...carOptionsData,
        body: carOptionsData?.title,
        price: getPriceLabel(carOptionsData),
        decoratorImgContent: 'VW',
      };
    }),
  };

  return (
    <>
      <Box
        mb={DesignTokenSizeEnum.static370}
        b960={{ mb: DesignTokenSizeEnum.static530 }}
      >
        <SectionHeader
          title={contentManager.get('key-features-section-title-normal')}
          titleBold={contentManager.get('key-features-section-title-bold')}
          titleSize={TokenTextAppearance.headline400}
          description={contentManager.get('key-features-section-text')}
          descriptionSize={TokenTextAppearance.bigcopy100}
        />
      </Box>
      <StyledKeyFeaturesSections>
        <FeatureCardsSection
          title={contentManager.get('your-exteriors-section-title')}
          sections={[exteriorPaintContent, exteriorWheelsContent]}
          sectionName={SectionNames.COMPARE_PAGE_EXTERIOR_FEATURES}
        />
        <FeatureCardsSection
          title={contentManager.get('your-interiors-section-title')}
          sections={[
            interiorUpholsteryContent,
            interiorEntertainmentContent,
            interiorSteeringWheelContent,
          ]}
          sectionName={SectionNames.COMPARE_PAGE_INTERIOR_FEATURES}
        />
      </StyledKeyFeaturesSections>
    </>
  );
};

export default Features;
